import { createUserDisplayName } from '@faro/app-context';
import { useAuth } from '@faro/auth';
import { FaroAvatar, FaroMenu, FaroNavigationLink, FaroOverlayTrigger } from '@faro/design-system';
import { ChartLineBarIcon, ChatQuestionOutlinedIcon, LogoutIcon, MoreVerticalIcon } from '@faro/icons';
import { StudySpaceAnalyticEventKey, useStudySpaceAnalytics } from '@faro/study-space-ui';
import { ComponentProps, FC, ReactNode } from 'react';
import styles from './UserMenu.module.scss';

export interface UserMenuProps extends ComponentProps<'button'> {
    /**
     * User information from auth
     **/
    user: MenuUser;

    /**
     * If true, component shows avatar, user name and role. If false, component only shows avatar .
     * Default: true
     **/
    isExpanded: boolean;

    /**
     * If provided, a user menu link to help center will be displayed and link to the provided address
     */
    helpCenterUrl?: string;
}

export interface MenuUser {
    firstName?: string;
    lastName?: string;
    email: string;
    roles: string[];
}

const UserMenu: FC<UserMenuProps> = (props): ReactNode | null => {
    const { user, helpCenterUrl, isExpanded = true, className = '', ...rest } = props;
    const auth = useAuth();
    const { trackEvent } = useStudySpaceAnalytics();

    if (user == null) {
        return null;
    }

    const onSystemStatusButtonClick = (): void => {
        trackEvent(StudySpaceAnalyticEventKey.clickSystemStatusLink);
        window.open('https://status.farohealth.com/', '_blank');
    };

    async function onLogoutButtonClick(): Promise<void> {
        trackEvent(StudySpaceAnalyticEventKey.logout);
        await auth?.logout();
    }

    const displayName = createUserDisplayName(user);
    return (
        <div data-automation-id="userMenu">
            <FaroOverlayTrigger
                openOnClick
                placement="right-end"
                overlay={
                    <FaroMenu>
                        {helpCenterUrl && (
                            <a href={helpCenterUrl}>
                                <FaroMenu.Item
                                    data-automation-id="helpCenterButton"
                                    left={<ChatQuestionOutlinedIcon />}
                                >
                                    Help Center
                                </FaroMenu.Item>
                            </a>
                        )}
                        <FaroMenu.Item
                            left={<ChartLineBarIcon />}
                            onClick={onSystemStatusButtonClick}
                            data-automation-id="systemStatusButton"
                            data-intercom-target="SystemStatus"
                        >
                            System status
                        </FaroMenu.Item>
                        <FaroMenu.Item
                            left={<LogoutIcon />}
                            onClick={onLogoutButtonClick}
                            data-automation-id="logoutButton"
                            data-intercom-target="Logout"
                        >
                            Log out
                        </FaroMenu.Item>
                    </FaroMenu>
                }
            >
                {(triggerProps, menuState) => (
                    <FaroNavigationLink
                        {...triggerProps}
                        className={`${styles.root} ${menuState.open ? styles.menuOpen : ''}`}
                        iconLeft={
                            <FaroAvatar
                                user={user}
                                className={styles.avatar}
                                showTooltip={false}
                                data-automation-id="avatar"
                            />
                        }
                        label={
                            <div className={styles.label}>
                                <span>{displayName}</span>
                                <MoreVerticalIcon />
                            </div>
                        }
                    />
                )}
            </FaroOverlayTrigger>
        </div>
    );
};

export default UserMenu;
